import { DialogContent, useTheme, useMediaQuery, Grid } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialog } from 'shell/components/shared/dialog';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import { FormControlLabel } from '@mui/material';
import { Checkbox } from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import TypeReason from './type-reason';
import CivicState from './civic-state';
import UrbanArea from './urban-area';
import CivicType from './civic-type';
import Municipality from './municipality';
import * as yup from 'yup';
import { regexOnlyNumber } from 'shell/constants/regex';
import { __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import { toast } from 'react-toastify';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { Civic } from 'shell/models/civic';
import LocalAreaToponymy from 'municipal-messenger/components/form/autocomplete-local-area';
import AutocompleteMessoList from './messo-list';
import { ItemVM } from 'shell/view-models/item';
import { MunicipalMessengerMessengerListItem } from 'shell/models/municipal-messenger-messenger-list';

interface NewCivicModalProps {
  open: boolean;
  onClose: (open?: boolean) => void;
  onYes: (item?: number) => void;
  onNo: (open?: boolean) => void;
  disabled?: boolean;
  circulationArea: number;
}

interface NewCivicFormVM {
  numberCivic?: string;
  cap?: string;
  temporary?: boolean;
  secondary?: boolean;
  institutionDate?: DateTime | null;
  institutionReason?: string;
  civicState?: string;
  zone?: string;
  civicType?: string;
  townHall?: string;
  electoralSection?: string;
  territorialArea?: string;
  municipalMessenger?: ItemVM<MunicipalMessengerMessengerListItem> | null;
  sectionCensimento?: string;
  localIdentity?: string;
}

const NewCivicModal = ({
  open,
  onYes,
  onNo,
  disabled,
  circulationArea,
}: NewCivicModalProps): React.ReactElement => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  const createCivic = useHttpClient(
    `${__TOPONYMY_API_URL__}/areaCircolazione/${circulationArea}/civico`,
    {
      options: {
        method: 'POST',
      },
    }
  );

  const formik = useFormik<NewCivicFormVM>({
    initialValues: {
      numberCivic: '',
      cap: '',
      institutionDate: null,
      institutionReason: '',
      townHall: '',
    },
    validationSchema: yup.object().shape({
      cap: yup.string().required('Campo obbligatorio').matches(regexOnlyNumber, 'Campo numerico'),
      numberCivic: yup.string().required('Campo obbligatorio'),
      townHall: yup.string().required('Campo obbligatorio'),
      institutionDate: yup
        .date()
        .nullable()
        .required('Campo obbligatorio')
        .typeError('Data non valida')
        .max(DateTime.now(), 'Non può essere maggiore di oggi'),
      institutionReason: yup.string().required('Campo obbligatorio'),
    }),
    onSubmit: (values) => {
      (
        createCivic({
          data: {
            key: {
              esponente: values.numberCivic,
              dataIstituzione: values.institutionDate?.toString().replace(/-/g, ''),
            },
            motivo: values.institutionReason,
            cap: values.cap,
            statoCivico: values.civicState || null,
            provvisorio: values.temporary ? 'S' : 'N',
            municipio: values.townHall,
            zonaUrbanistica: values.zone || null,
            tipologiaAbitazione: values.civicType,
            sezioneElettorale: values.electoralSection || null,
            ambitoTerritoriale: values.territorialArea || null,
            flag: values.institutionReason,
            sezCensim: values.sectionCensimento || null,
            dsb: values.localIdentity || null,
          },
        }) as ResponsePromise<HttpResponse<Civic>>
      ).then((response) => {
        toast.success('Civico creato correttamente');
        onYes(response?.data.payload?.key?.numero);
      });
    },
  });

  return open ? (
    <PmiDialog
      open={open}
      fullScreen={fullScreen}
      //onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        Nuovo Civico
      </PmiDialogTitle>
      <DialogContent style={{ padding: '22px 24px' }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} container spacing={0}>
            <Grid item xs={12} md={6}>
              <PmiTextField
                label="Numero civico *"
                id="numberCivic"
                name="numberCivic"
                fullWidth
                type="text"
                variant="outlined"
                value={formik.values.numberCivic || ''}
                onChange={formik.handleChange}
                error={formik.touched.numberCivic && Boolean(formik.errors.numberCivic)}
                helperText={formik.errors.numberCivic}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <PmiTextField
              label="CAP *"
              id="cap"
              name="cap"
              fullWidth
              type="text"
              variant="outlined"
              value={formik.values.cap || ''}
              onChange={formik.handleChange}
              error={formik.touched.cap && Boolean(formik.errors.cap)}
              helperText={formik.errors.cap}
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="temporary"
                  name="temporary"
                  checked={formik.values.temporary || false}
                  onChange={(event) => {
                    const form: NewCivicFormVM = {
                      ...formik.values,
                      temporary: !!event.target.checked,
                    };
                    formik.setValues(form);
                  }}
                  inputProps={{ 'aria-label': 'provvisorio' }}
                />
              }
              label="Provvisorio"
            />
          </Grid>
          {/* <Grid item xs={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="entered"
                  name="entered"
                  checked={formik.values.secondary || false}
                  onChange={(event) => {
                    const form: NewCivicFormVM = {
                      ...formik.values,
                      secondary: !!event.target.checked,
                    };
                    formik.setValues(form);
                  }}
                  inputProps={{ 'aria-label': 'seconda entrata' }}
                />
              }
              label="II Entrata"
            />
          </Grid> */}
          <Grid
            container
            spacing={0}
            rowSpacing={{ xs: 1, md: 0 }}
            columnSpacing={{ xs: 0, md: 1 }}
            item
            xs={12}
          >
            <Grid item xs={12} md={6}>
              <DatePicker
                label="Data Istituzione *"
                value={formik.values.institutionDate || null}
                onChange={(value) => {
                  if (value) {
                    const dateISO = value.toISODate();
                    formik.setFieldValue('institutionDate', dateISO);
                  }
                }}
                renderInput={(params) => (
                  <PmiTextField
                    id="institutionDate"
                    name="institutionDate"
                    {...params}
                    error={formik.touched.institutionDate && Boolean(formik.errors.institutionDate)}
                    helperText={formik.errors.institutionDate}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TypeReason
                id="institutionReason"
                name="institutionReason"
                label="Motivo Istituzione"
                required
                value={formik.values.institutionReason || ''}
                onChange={formik.handleChange}
                error={formik.touched.institutionReason && Boolean(formik.errors.institutionReason)}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <CivicState
              disabled={disabled}
              value={formik.values.civicState || ''}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Municipality
              disabled={disabled}
              value={formik.values.townHall || ''}
              onChange={formik.handleChange}
              id="townHall"
              required
              error={formik.touched.townHall && Boolean(formik.errors.townHall)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <UrbanArea
              disabled={disabled}
              value={formik.values.zone || ''}
              onChange={formik.handleChange}
              id="zone"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CivicType
              disabled={disabled}
              value={formik.values.civicType || ''}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Sezione Elettorale"
              id="electoralSection"
              name="electoralSection"
              type="text"
              fullWidth
              variant="outlined"
              value={formik.values.electoralSection || ''}
              disabled={disabled}
              onChange={formik.handleChange}
              error={formik.touched.electoralSection && Boolean(formik.errors.electoralSection)}
              helperText={formik.errors.electoralSection}
            />
          </Grid>
          <Grid item xs={12} container spacing={1}>
            <Grid item xs={12} md={6}>
              <LocalAreaToponymy
                value={formik.values.territorialArea || ''}
                disabled={disabled}
                onChange={(value) => formik.setFieldValue('territorialArea', value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <AutocompleteMessoList
                label="Messo"
                id="municipalMessenger"
                value={formik.values.municipalMessenger || null}
                disabled
                ambitoTerritoriale={formik.values.territorialArea || ''}
                onChange={(value) => formik.setFieldValue('municipalMessenger', value)}
                error={
                  formik.touched.municipalMessenger && Boolean(formik.errors.municipalMessenger)
                }
                helperText={formik.errors.municipalMessenger}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Sezione censimento"
              id="censusSection"
              name="sectionCensimento"
              type="text"
              fullWidth
              variant="outlined"
              value={formik.values.sectionCensimento || ''}
              disabled={disabled}
              onChange={formik.handleChange}
              error={formik.touched.sectionCensimento && Boolean(formik.errors.sectionCensimento)}
              helperText={formik.errors.sectionCensimento}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Nucleo Identità Locale"
              id="localIdentity"
              name="localIdentity"
              type="text"
              fullWidth
              variant="outlined"
              value={formik.values.localIdentity || ''}
              disabled={disabled}
              onChange={formik.handleChange}
              error={formik.touched.localIdentity && Boolean(formik.errors.localIdentity)}
              helperText={formik.errors.localIdentity}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <div className="pmi-d-flex pmi-justify-content-end pmi-pb-3 pmi-px-3">
        <div className="pmi-mr-3 pmi-pr-1 pmi-w-25">
          <PmiButton
            fullWidth
            onClick={() => {
              formik.handleSubmit();
            }}
            variant="contained"
            color="primary"
            disabled={disabled}
          >
            Conferma
          </PmiButton>
        </div>
        <div className="pmi-w-25">
          <PmiButton fullWidth onClick={() => onNo(false)} variant="outlined" color="cdmBrand">
            Annulla
          </PmiButton>
        </div>
      </div>
    </PmiDialog>
  ) : (
    <></>
  );
};

NewCivicModal.displayName = 'New Civic Modal component';

export default NewCivicModal;
